import { defineStore } from "pinia";
import {
  createLicenseOption,
  generateDeveloperLicense,
  getAllLicenseOptions,
  updateLicenseOption,
  updateLicenseOptionStatus,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withLoading } from "~/helpers/withLoading";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import type {
  WebModelGetAppLicenseOption,
  WebModelPutLicenseOption,
  WebModelPutLicenseOptionStatus,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";

type State = {
  licenseOptionsForPublicAppsInCompany: WebModelGetAppLicenseOption[];
};

export const useLicenseOptionsStore = defineStore("licenseoptions", {
  // convert to a function
  state: (): State => ({
    licenseOptionsForPublicAppsInCompany: [],
  }),
  getters: {},
  actions: {
    async loadAllLicenseOptionsForPublishedAppsInCompany(companyId: string) {
      if (!companyId) {
        console.warn("User has no company ID, returning empty license options");
        this.licenseOptionsForPublicAppsInCompany = [];
        return;
      }
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(getAllLicenseOptions(companyId))),
      );
      console.debug(
        "loadAllLicenseOptionsForPublishedAppsInCompany",
        data,
        error,
      );
      if (!error) {
        this.licenseOptionsForPublicAppsInCompany = data ?? [];
      }
    },
    async createLicenseOption(appId: number) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(createLicenseOption(appId))),
      );
      console.debug("createLicenseOption: ", data, error);
      if (!error) {
        return data;
      }
      return false;
    },
    async updateLicenseOption(
      companyId: string,
      appId: number,
      licenseOptionId: number,
      webModelPutLicenseOption: WebModelPutLicenseOption,
    ) {
      const { data } = await withErrorMessage(
        withLoading(
          updateLicenseOption(appId, licenseOptionId, webModelPutLicenseOption),
        ),
      );
      console.debug("updateLicenseOption: ", data);
      if (data) {
        await this.loadAllLicenseOptionsForPublishedAppsInCompany(companyId);
      }
      return data;
    },
    async updateLicenseOptionStatus(
      companyId: string,
      appId: number,
      licenseOptionId: number,
      webModelPutLicenseOptionStatus: WebModelPutLicenseOptionStatus,
    ) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            updateLicenseOptionStatus(
              appId,
              licenseOptionId,
              webModelPutLicenseOptionStatus,
            ),
          ),
        ),
      );
      console.debug("updateLicenseOptionStatus: ", data, error);
      if (error) {
        return false;
      }
      if (data) {
        await this.loadAllLicenseOptionsForPublishedAppsInCompany(companyId);
      }
      return data;
    },
    async generateDeveloperLicense(appId: number, licenseOptionId: number) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(generateDeveloperLicense(appId, licenseOptionId)),
        ),
      );
      console.debug("generateDeveloperLicense: ", data, error);
      return !error;
    },
    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
  },
});
